// src/services/superAdminDashboardService.js

import { repo } from "../dbservice/repo";

class SuperAdminDashboardService {
  constructor() {
    this._activeSubscriptions = {};
  }

  /* ─────────────────────────────────────────────────────────────────────────
     SUBSCRIPTIONS — real-time listeners via repo
  ───────────────────────────────────────────────────────────────────────── */

  /**
   * Subscribe to all companies (superadmin only — no companyId filter).
   * @param {Function} callback - (companies[]) => void
   * @returns {Function} unsubscribe
   */
  subscribeToCompanies(callback) {
    return repo.subscribeToUpdates(
      "companies",
      [],
      [{ field: "createdAt", order: "desc" }],
      null,
      callback,
    );
  }

  /**
   * Subscribe to all users.
   * @param {Function} callback - (users[]) => void
   * @returns {Function} unsubscribe
   */
  subscribeToUsers(callback) {
    return repo.subscribeToUpdates(
      "users",
      [],
      [],
      null,
      callback,
    );
  }

  /**
   * Subscribe to all assets.
   * @param {Function} callback - (assets[]) => void
   * @returns {Function} unsubscribe
   */
  subscribeToAssets(callback) {
    return repo.subscribeToUpdates(
      "assets",
      [],
      [],
      null,
      callback,
    );
  }
  /**
   * Subscribe to access requests (pending admin/user requests).
   */
  subscribeToAccessRequests(callback) {
    return repo.subscribeToUpdates(
      "accessRequests",
      [],
      [{ field: "createdAt", order: "desc" }],
      null,
      callback,
    );
  }

  /**
   * Subscribe to recent audit logs (latest 10).
   * @param {Function} callback - (logs[]) => void
   * @returns {Function} unsubscribe
   */
  subscribeToAuditLogs(callback) {
    return repo.subscribeToUpdates(
      "auditLogs",
      [],
      [{ field: "timestamp", order: "desc" }],
      10,
      callback,
    );
  }

  /* ─────────────────────────────────────────────────────────────────────────
     ONE-TIME FETCH (fallback / on-demand)
  ───────────────────────────────────────────────────────────────────────── */

  async fetchAllCompanies() {
    return await repo.fetchData(
      "companies",
      [],
      [{ field: "createdAt", order: "desc" }],
    );
  }

  async fetchAllUsers() {
    return await repo.fetchData("users", [], []);
  }

  async fetchAllAssets() {
    return await repo.fetchData("assets", [], []);
  }

  async fetchRecentAuditLogs(limitVal = 10) {
    return await repo.fetchData(
      "auditLogs",
      [],
      [{ field: "timestamp", order: "desc" }],
      limitVal,
    );
  }

  async fetchAssetsByCompany(companyId) {
    return await repo.fetchData("assets", [
      { field: "companyId", value: companyId },
    ]);
  }

  async fetchUsersByCompany(companyId) {
    return await repo.fetchData("users", [
      { field: "companyId", value: companyId },
    ]);
  }

  /* ─────────────────────────────────────────────────────────────────────────
     PURE DATA PROCESSORS
     (stateless helpers — take raw arrays, return derived data)
  ───────────────────────────────────────────────────────────────────────── */

  /**
   * Build summary KPI metrics from raw data arrays.
   */
  buildDashboardMetrics(companies, users, assets) {
    const totalValue = assets.reduce((sum, a) => {
      return sum + (parseFloat(a.purchaseValue || a.cost) || 0);
    }, 0);

    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

    const activeAlerts = assets.filter((a) => {
      if (!a.warrantyExpiry) return false;
      try {
        const exp = a.warrantyExpiry?.toDate
          ? a.warrantyExpiry.toDate()
          : new Date(a.warrantyExpiry);
        return exp <= thirtyDaysFromNow && exp >= now;
      } catch {
        return false;
      }
    }).length;

    return {
      totalCompanies:  companies.length,
      totalUsers:      users.length,
      totalAssets:     assets.length,
      totalValue,
      activeAlerts,
      systemHealth:    "Excellent",
      monthlyGrowth:   this._calcMonthlyGrowthPct(assets),
    };
  }

  /**
   * Build user role distribution for charts.
   * @param {Array}  users      - raw users array
   * @param {String} companyId  - optional; if provided, only users of that company
   */
  buildUserDistribution(users, companyId = null) {
    const roleColors = {
      superadmin:    "#8B5CF6",
      admin:         "#3B82F6",
      user:          "#10B981",
      manager:       "#F59E0B",
      viewer:        "#6B7280",
      unknown:       "#EF4444",
    };

    const roleDisplayNames = {
      superadmin: "Super Admins",
      admin:      "Company Admins",
      user:       "General Users",
      manager:    "Managers",
      viewer:     "Viewers",
      unknown:    "Unknown Role",
    };

    // Filter by company if a specific company is selected
    const scopedUsers = companyId
      ? users.filter((u) => u.companyId === companyId)
      : users;

    const roleCounts = {};
    scopedUsers.forEach((u) => {
      const role = u.role || "unknown";
      roleCounts[role] = (roleCounts[role] || 0) + 1;
    });

    const total = scopedUsers.length || 1;

    return Object.entries(roleCounts)
      .map(([role, count]) => ({
        role:       roleDisplayNames[role] || role,
        count,
        color:      roleColors[role] || "#6B7280",
        percentage: ((count / total) * 100).toFixed(1),
      }))
      .sort((a, b) => b.count - a.count);
  }
  /**
   * Build per-company summary rows for the overview table.
   */
  buildCompaniesTableData(companies, users, assets) {
    return companies.map((company) => {
      const cId = company.companyId || company.id;

      const companyUsers = users.filter(
        (u) => u.companyId === cId || u.companyId === company.id,
      );

      const companyAssets = assets.filter(
        (a) => a.companyId === cId || a.companyId === company.id,
      );

      const value = companyAssets.reduce(
        (sum, a) => sum + (parseFloat(a.purchaseValue || a.cost) || 0),
        0,
      );

      return {
        id:            company.id,
        companyId:     cId,
        name:          company.name          || "Unknown Company",
        contactPerson: company.contactPerson || company.contactPersonName || "N/A",
        email:         company.email         || "N/A",
        status:        company.status        || "Active",
        users:         companyUsers.length,
        assets:        companyAssets.length,
        value,
        createdAt:     company.createdAt,
        updatedAt:     company.updatedAt,
      };
    });
  }

  /**
   * Build company utilization/performance rows (for bar chart).
   */
  buildCompanyPerformance(companies, assets) {
    const companyMap = {};

    assets.forEach((a) => {
      const cId = a.companyId || "Unknown";
      if (!companyMap[cId]) {
        const info = companies.find(
          (c) => (c.companyId || c.id) === cId || c.id === cId,
        );
        companyMap[cId] = {
          name:   info?.name || cId,
          total:  0,
          active: 0,
          value:  0,
        };
      }
      companyMap[cId].total++;
      companyMap[cId].value += parseFloat(a.purchaseValue || a.cost) || 0;
      const s = (a.status || "").toLowerCase();
      if (s === "available" || s === "active") companyMap[cId].active++;
    });

    return Object.entries(companyMap)
      .map(([cId, d]) => ({
        company:     d.name.length > 15 ? d.name.slice(0, 15) + "…" : d.name,
        fullName:    d.name,
        companyId:   cId,
        assets:      d.total,
        activeAssets:d.active,
        utilization: d.total > 0 ? Math.round((d.active / d.total) * 100) : 0,
        totalValue:  d.value,
      }))
      .sort((a, b) => b.utilization - a.utilization);
  }

  /**
   * Build 6-month cumulative growth trend data for the area chart.
   */
  buildGrowthTrend(companies, users, assets) {
    const now      = new Date();
    const hasData  = companies.length > 0 || users.length > 0 || assets.length > 0;
    const months   = [];

    for (let i = 5; i >= 0; i--) {
      const date      = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const nextMonth = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
      const label     = date.toLocaleDateString("en-US", {
        month: "short", year: "2-digit",
      });

      if (hasData) {
        const cumCompanies  = this._countBefore(companies, nextMonth);
        const cumUsers      = this._countBefore(users,     nextMonth);
        const cumAssets     = this._countBefore(assets,    nextMonth, true);

        const monthCompanies = this._countInRange(companies, date, nextMonth);
        const monthUsers     = this._countInRange(users,     date, nextMonth);
        const monthAssets    = this._countInRange(assets,    date, nextMonth, true);

        months.push({
          month:   label,
          companies: Math.max(0, cumCompanies),
          users:     Math.max(0, cumUsers),
          assets:    Math.max(0, cumAssets),
          monthlyGrowth: {
            companies: monthCompanies,
            users:     monthUsers,
            assets:    monthAssets,
          },
        });
      } else {
        // Synthetic sample data when no real records exist
        const base = 5 - i;
        months.push({
          month:   label,
          companies: base * 2 + 1,
          users:     base * 5 + 3,
          assets:    base * 10 + 5,
          monthlyGrowth: { companies: 1, users: 3, assets: 5 },
        });
      }
    }

    return months;
  }

  /**
   * Process raw audit log docs into UI-friendly activity rows.
   */
  buildRecentActivities(logs) {
    if (!logs || logs.length === 0) return this._defaultActivities();

    return logs.slice(0, 5).map((log, i) => {
      let ts = new Date();
      try {
        ts = log.timestamp?.toDate
          ? log.timestamp.toDate()
          : new Date(log.timestamp || Date.now());
      } catch { /* ignore */ }

      return {
        id:        i + 1,
        type:      log.action || log.type || "System Activity",
        details:   log.details || log.description || log.message || "Activity performed",
        timestamp: this._timeAgo(ts),
        user:      log.userEmail || log.performedBy || log.user || "System",
      };
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     PRIVATE HELPERS
  ───────────────────────────────────────────────────────────────────────── */

  _parseDate(item, useAssetFallback = false) {
    try {
      if (item.createdAt) {
        return item.createdAt?.toDate
          ? item.createdAt.toDate()
          : new Date(item.createdAt);
      }
      if (useAssetFallback && item.purchaseDate) {
        return item.purchaseDate?.toDate
          ? item.purchaseDate.toDate()
          : new Date(item.purchaseDate);
      }
    } catch { /* ignore */ }
    return null;
  }

  _countBefore(items, beforeDate, useAssetFallback = false) {
    return items.filter((item) => {
      const d = this._parseDate(item, useAssetFallback);
      return d === null || d < beforeDate; // include undated items
    }).length;
  }

  _countInRange(items, from, to, useAssetFallback = false) {
    return items.filter((item) => {
      const d = this._parseDate(item, useAssetFallback);
      return d !== null && d >= from && d < to;
    }).length;
  }

  _calcMonthlyGrowthPct(assets) {
    const now       = new Date();
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

    const thisCount = this._countInRange(assets, thisMonth, now, true);
    const lastCount = this._countInRange(assets, lastMonth, thisMonth, true);

    if (lastCount === 0) return thisCount > 0 ? 100 : 0;
    return Math.round(((thisCount - lastCount) / lastCount) * 100);
  }

  _timeAgo(date) {
    try {
      const diff = Math.floor((Date.now() - date.getTime()) / 1000);
      if (diff < 60)    return "Just now";
      if (diff < 3600)  return `${Math.floor(diff / 60)} minutes ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
      return `${Math.floor(diff / 86400)} days ago`;
    } catch {
      return "Unknown";
    }
  }

  _defaultActivities() {
    return [
      { id: 1, type: "System Health",  details: "System performance check completed successfully", timestamp: "1 hour ago",   user: "System" },
      { id: 2, type: "Database",       details: "Daily database backup completed",                  timestamp: "6 hours ago",  user: "System" },
      { id: 3, type: "Security",       details: "Security scan completed — no threats detected",    timestamp: "12 hours ago", user: "System" },
      { id: 4, type: "Maintenance",    details: "Automated system maintenance completed",            timestamp: "1 day ago",    user: "System" },
      { id: 5, type: "Monitoring",     details: "System monitoring alerts configured",              timestamp: "2 days ago",   user: "System" },
    ];
  }

   calcGrowthPercentage(previous, current) {
    if (previous === 0) return current > 0 ? 100 : 0;
    const pct = ((current - previous) / previous) * 100;
    // Return 0 if it's a tiny fraction, otherwise round to nearest integer
    return Math.abs(pct) < 0.5 ? 0 : Math.round(pct);
  }

  formatCurrency(value) {
    if (value >= 10_000_000) return `₹${(value / 10_000_000).toFixed(1)}Cr`;
    if (value >= 100_000)    return `₹${(value / 100_000).toFixed(1)}L`;
    if (value >= 1_000)      return `₹${(value / 1_000).toFixed(1)}K`;
    return `₹${value.toLocaleString()}`;
  }

  countAddedThisMonth(items, useAssetFallback = false) {
    const start = new Date();
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    return this._countInRange(items, start, new Date(), useAssetFallback);
  }
}

export const dashboardService = new SuperAdminDashboardService();